import ReactApexChart from 'react-apexcharts';
import { withNamespaces } from 'react-i18next';
import {
    Card,
    CardBody,
    CardTitle,
} from 'reactstrap';

import usePackagingTonnageTrend from './hooks/usePackagingTonnageTrend';

const PackagingTonnageTrendSkeleton = () => (
    <Card className="h-100 mb-0">
        <CardBody>
            <div className="dt-field dt-skeleton mb-2" style={{ width: '35%', height: '24px' }} />
            <div className="dt-field dt-skeleton mb-4" style={{ width: '45%', height: '16px' }} />
            <div className="dt-field dt-skeleton" style={{ width: '100%', height: '280px' }} />
        </CardBody>
    </Card>
);

export const PackagingTonnageTrendChart = ({
    t,
    categories = [],
    chartSeries = [],
    isLoading = false,
    isFetching = false,
    isError = false,
}) => {
    if (isLoading || isFetching) {
        return <PackagingTonnageTrendSkeleton />;
    }

    if (isError || !categories.length || !chartSeries.length) {
        return null;
    }

    const options = {
        chart: {
            height: 320,
            toolbar: {
                show: false,
            },
            type: 'line',
            zoom: {
                enabled: false,
            },
        },
        colors: chartSeries.map((item) => item.color).filter(Boolean),
        legend: {
            show: false,
        },
        dataLabels: {
            enabled: false,
        },
        stroke: {
            curve: 'smooth',
            width: 3,
        },
        markers: {
            size: 4,
            hover: {
                size: 6,
            },
        },
        grid: {
            row: {
                colors: ['#f3f3f3', 'transparent'],
                opacity: 0.5,
            },
        },
        xaxis: {
            categories,
            title: {
                text: t('Months'),
                style: {
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#828282',
                },
            },
        },
        yaxis: {
            min: 0,
            forceNiceScale: true,
            decimalsInFloat: 1,
            title: {
                text: t('Packaging tonnage (t)'),
                style: {
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#828282',
                },
            },
            labels: {
                formatter: (value) => Number(value).toFixed(1),
            },
        },
        tooltip: {
            y: {
                formatter: (value) => `${Number(value).toFixed(1)} t`,
            },
        },
    };

    return (
        <Card className="h-100 mb-0">
            <CardBody>
                <div className="mb-4">
                    <CardTitle tag="h6" className="mb-1">
                        {t('Packaging Tonnage Trend')}
                    </CardTitle>
                    <p className="mb-0 text-muted" style={{ fontSize: '13px' }}>
                        {t('Monthly packaging placed on the market')}
                    </p>
                </div>

                <div className="apex-charts">
                    <ReactApexChart
                        options={options}
                        series={chartSeries}
                        type="line"
                        height="280"
                    />
                </div>
            </CardBody>
        </Card>
    );
};

const PackagingTonnageTrend = ({
    t,
}) => {
    const {
        categories,
        chartSeries,
        isLoading,
        isFetching,
        isError,
    } = usePackagingTonnageTrend({ t });

    return (
        <PackagingTonnageTrendChart
            t={t}
            categories={categories}
            chartSeries={chartSeries}
            isLoading={isLoading}
            isFetching={isFetching}
            isError={isError}
        />
    );
};

export default withNamespaces()(PackagingTonnageTrend);
